import { useEffect, useRef, useState } from "react"
import { AlertTriangle, RefreshCw } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@f2e/ui/components/base"
import { useGetLineQuotaApi } from "@/hooks/api/clientChannels"
import { FetchStatus } from "@/hooks/useFetch"
import { getPermissionDeniedTranslationKey } from "@/lib/apiErrors"

export interface LineQuotaNoticeProps {
  organizationId: string
  workspaceId: string
  conversationKey: string
  channel?: { id?: string | null; type: string } | null
}

// Remount per conversation so opening another thread checks again, while the
// backend shares its short-lived cache across conversations on the same channel.
export const LineQuotaNotice = (props: LineQuotaNoticeProps) => {
  if (props.channel?.type !== "line" || !props.channel.id) return null
  return (
    <ChannelLineQuotaNotice
      key={`${props.organizationId}:${props.workspaceId}:${props.channel.id}:${props.conversationKey}`}
      {...props}
      channelId={props.channel.id}
    />
  )
}

const ChannelLineQuotaNotice = ({
  organizationId,
  workspaceId,
  channelId,
}: LineQuotaNoticeProps & { channelId: string }) => {
  const { t, i18n } = useTranslation()
  const requestController = useRef<AbortController | null>(null)
  const [refreshFailureKey, setRefreshFailureKey] = useState<string | null>(
    null
  )
  const { data, error, status, fetchFn } = useGetLineQuotaApi(
    organizationId,
    workspaceId,
    channelId
  )
  useEffect(() => {
    const controller = new AbortController()
    requestController.current = controller
    void fetchFn({ signal: controller.signal }).catch(() => undefined)
    return () => controller.abort()
  }, [fetchFn])

  const quota = data?.data
  const failureKey =
    getPermissionDeniedTranslationKey(error) ??
    "history.platformReply.lineQuota.unavailable"
  const unavailable =
    status === FetchStatus.Error ||
    quota?.status === "unavailable" ||
    (status === FetchStatus.Loading && refreshFailureKey !== null)
  const nearLimit =
    quota?.status === "available" &&
    quota.quota_type === "limited" &&
    quota.limit !== null &&
    quota.total_usage !== null &&
    quota.total_usage >= quota.limit * 0.9
  if (!unavailable && !nearLimit) return null

  const reachedLimit = nearLimit && quota.total_usage! >= quota.limit!
  const loading = status === FetchStatus.Loading
  return (
    <div className="mb-3 rounded-md border border-amber-300/70 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200">
      <div role="status" className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <div>
          <p>
            {unavailable
              ? t(
                  status === FetchStatus.Loading && refreshFailureKey
                    ? refreshFailureKey
                    : failureKey
                )
              : t(
                  `history.platformReply.lineQuota.${reachedLimit ? "reached" : "near"}`
                )}
          </p>
          {!unavailable && quota && (
            <p className="mt-1 text-xs">
              {t("history.platformReply.lineQuota.usage", {
                usage: quota.total_usage!.toLocaleString(i18n.language),
                limit: quota.limit!.toLocaleString(i18n.language),
                time: new Date(quota.checked_at).toLocaleString(i18n.language),
              })}
            </p>
          )}
        </div>
      </div>
      <div className="mt-1 flex items-center gap-3 pl-6">
        <a
          href="https://manager.line.biz/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs underline underline-offset-4"
        >
          {t("history.platformReply.lineQuota.checkAccount")}
        </a>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-1 text-xs"
          disabled={loading}
          onClick={() => {
            setRefreshFailureKey(
              status === FetchStatus.Error ? failureKey : null
            )
            void fetchFn({
              signal: requestController.current?.signal,
              searchParams: new URLSearchParams({ refresh: "true" }),
              preserveData: true,
            })
              .catch(() => undefined)
              .finally(() => setRefreshFailureKey(null))
          }}
        >
          <RefreshCw
            className={loading ? "h-3 w-3 animate-spin" : "h-3 w-3"}
            aria-hidden="true"
          />
          {t("history.platformReply.lineQuota.refresh")}
        </Button>
      </div>
    </div>
  )
}
